// InternationalSEOPage.jsx
import React, { useEffect } from 'react';
import { Row, Col, Button, Table } from 'reactstrap';
import { Link } from 'react-router-dom';
import xsimage from '../../assets/images/seoxs.PNG'

const InternationalSEOPage = () => {
    useEffect(() => {
        window.scroll(0, 0)

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.style.animation = 'fadeInUp 0.8s ease-out forwards';
                }
            });
        }, { threshold: 0.1 });

        document.querySelectorAll('[data-animated]').forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const intlStats = [
        { number: '65%', label: 'higher engagement from localised content vs translated content' },
        { number: '3x', label: 'more organic traffic with proper hreflang implementation' },
        { number: '72%', label: 'of global consumers prefer websites in their native language' },
        { number: '50%', label: 'reduction in bounce rate with culturally relevant content' }
    ];

    const intlApproaches = [
        {
            icon: 'fas fa-globe-americas',
            title: 'Market & Competitor Analysis',
            desc: 'We start by understanding your global goals and analyzing competitive landscapes in target countries, including local search behavior, competitor performance, and market opportunities.'
        },
        {
            icon: 'fas fa-search',
            title: 'International Keyword Research',
            desc: 'Multi-language keyword research based on cultural nuance and search intent, ensuring your SEO targets align with how users in each market truly search and find information.'
        },
        {
            icon: 'fas fa-cogs',
            title: 'Technical International SEO',
            desc: 'Implementation of hreflang tags, geo-targeting signals, international-friendly site architecture, and proper URL structures for effective cross-border search performance.'
        },
        {
            icon: 'fas fa-language',
            title: 'Multilingual & Localised Content',
            desc: 'Culturally relevant, locally accurate content that resonates with each audience—going beyond translation to create authentic engagement and enhanced organic visibility.'
        },
        {
            icon: 'fas fa-chart-network',
            title: 'Geo-Specific Link Building',
            desc: 'Building authoritative backlinks from relevant regional domains and local language sources to establish credibility and authority in each target market.'
        },
        {
            icon: 'fas fa-chart-line',
            title: 'Continuous Optimization & Monitoring',
            desc: 'Ongoing performance monitoring across regions, refinement based on data, and adaptation to new search trends and market shifts for long-term international success.'
        }
    ];

    const benefits = [
        ['Global Search Visibility', 'Targeted audiences find you across countries through region-specific search results'],
        ['Increased Organic Engagement', 'More relevant traffic from key markets with higher conversion potential'],
        ['Market-Specific Relevance', 'Content and experience tailored to cultural nuances and local preferences'],
        ['Competitive Positioning', 'Establish your brand as the trusted choice in every target region'],
        ['Sustainable Global Growth', 'Long-term SEO performance that scales with your international expansion']
    ];

    const targetBusinesses = [
        'Businesses expanding beyond domestic markets',
        'E-commerce and SaaS brands with global demand',
        'Companies targeting multi-language audiences',
        'Organizations in competitive international landscapes',
        'Enterprises seeking long-term brand recognition worldwide',
        'Brands with physical presence in multiple countries'
    ];

    const intlQuestions = [
        'Are you visible in search results across your target international markets?',
        'Is your content localized for cultural relevance or just translated?',
        'Do you have the correct hreflang implementation for multi-region targeting?',
        'How does your international search performance compare to local competitors?',
        'What technical setup best supports your global expansion goals?'
    ];

    return (
        <div className="seo sv-page">
            <div className="sv-decorative-image-container">

            </div>
            <div className='xs-sv-page'>
                <img src={xsimage} />
            </div>
            <div className="sv-page-title-section">
                <div className="container">
                    <h1>
                        <span className="sv-gradient-text">International SEO</span> Services
                    </h1>
                    <p>Expand your reach across borders and grow in global markets</p>
                </div>
            </div>

            <div className="container-fluid sv-content-wrapper">
                <div className="sv-main-content">
                    <section className="sv-hero-section" data-animated>
                        <Row className="align-items-center">
                            <Col lg={8}>
                                <p className="sv-lead">
                                    <span className="sv-keyword-highlight">International SEO</span> helps your website achieve organic visibility in search engines across multiple countries and languages. In today's digital world, international visibility isn't optional — it's essential for scaling your business beyond local boundaries.
                                </p>
                                <p className="sv-description">
                                    At Reachtix, our <strong>International SEO services</strong> are designed to help brands enter new markets, engage global audiences, and drive sustainable growth — by combining technical expertise, cultural understanding, and data-driven strategy.
                                </p>
                                <p className="sv-description">
                                    International SEO goes far beyond translation — it's about localising content, technical setup, search behavior, and cultural relevance to each market you want to reach.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-rocket mr-2"></i>
                                        Start Global Expansion
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-comments mr-2"></i>
                                        Get Free Consultation
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    <section className="sv-stats-section">
                        <h2 className="sv-section-title" data-animated>
                            Why <span className="sv-text-gradient">International SEO Matters</span> for Your Business
                        </h2>
                        <Row className="sv-stats-grid">
                            {intlStats.map((stat, index) => (
                                <Col lg={6} md={6} key={index} className="mb-4" data-animated>
                                    <div className="sv-stat-card">
                                        <div className="sv-stat-number">{stat.number}</div>
                                        <div className="sv-stat-label">{stat.label}</div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </section>

                    <section className="sv-explanation-section" data-animated>
                        <Row>
                            <Col lg={8}>
                                <h2 className="sv-section-title">What Is International SEO?</h2>
                                <p className="sv-section-description">
                                    <strong>International SEO</strong> is the process of optimizing your website so that search engines clearly understand which countries and languages you are targeting, and serve the most relevant version of your content to users based on their location and language preferences.
                                </p>
                                <div className="sv-questions-grid">
                                    <h4>Key Questions Our International SEO Services Answer:</h4>
                                    {intlQuestions.map((question, index) => (
                                        <div key={index} className="sv-question-item">
                                            <i className="fas fa-question-circle"></i>
                                            {question}
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    <section className="sv-benefits-overview" data-animated>
                        <Row>
                            <Col xl={12}>
                                <h2 className="sv-section-title">Why Your Business Needs International SEO</h2>
                                <p className="sv-section-description">
                                    Expanding your SEO globally offers huge strategic advantages for sustainable growth. Connect with audiences in different countries and languages, attract qualified users from diverse regions, and stay ahead of competitors.
                                </p>
                                <div className="sv-benefits-cards">
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-globe"></i>
                                        <h5>Reach New Markets</h5>
                                        <p>Connect with audiences in different countries and languages</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-chart-line"></i>
                                        <h5>Increase Global Traffic</h5>
                                        <p>Attract qualified users from diverse regions</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-users"></i>
                                        <h5>Enhance Local Relevance</h5>
                                        <p>Tailor content and experience for individual markets</p>
                                    </div>
                                    <div className="sv-benefit-card">
                                        <i className="fas fa-trophy"></i>
                                        <h5>Gain Competitive Advantage</h5>
                                        <p>Stay ahead of competitors in major regions</p>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    <section className="sv-approach-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Our International SEO Approach at Reachtix</h2>
                                <p className="sv-section-description">
                                    We build international SEO strategies that are customized, scalable, and performance-focused for global market success.
                                </p>

                                <div className="sv-approaches-grid">
                                    {intlApproaches.map((item, index) => (
                                        <div key={index} className="sv-approach-card">
                                            <div className="sv-approach-icon">
                                                <i className={item.icon}></i>
                                            </div>
                                            <div className="sv-approach-content">
                                                <h4>{item.title}</h4>
                                                <p>{item.desc}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </section>

                    <section className="sv-benefits-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="mt-5 sv-section-title">Key Benefits of International SEO</h2>
                                <div className="table-responsive">
                                    <Table borderless className="sv-benefits-table">
                                        <thead>
                                            <tr>
                                                <th>Strategic Advantage</th>
                                                <th>Business Impact</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {benefits.map(([advantage, impact], index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <i className="fas fa-check-circle"></i>
                                                        <strong>{advantage}</strong>
                                                    </td>
                                                    <td>{impact}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </Col>
                        </Row>
                    </section>

                    <section className="sv-target-section" data-animated>
                        <Row>
                            <Col>
                                <h2 className="sv-section-title">Who Should Invest in International SEO?</h2>
                                <p className="sv-section-description">
                                    International SEO is essential for businesses that want sustainable global growth:
                                </p>
                                <div className="sv-target-list">
                                    {targetBusinesses.map((business, index) => (
                                        <div key={index} className="sv-target-item">
                                            <i className="fas fa-chevron-right"></i>
                                            {business}
                                        </div>
                                    ))}
                                </div>
                                <p className="sv-section-description mt-4">
                                    Whether you're launching into a new country or optimizing for multiple language audiences, International SEO is the bridge to global discoverability.
                                </p>
                            </Col>
                        </Row>
                    </section>

                    <section className="sv-main-cta" data-animated>
                        <Row>
                            <Col>
                                <h2>Ready to Take Your SEO Global?</h2>
                                <p className="sv-cta-description">
                                    Global visibility begins with a strategy built for search engines and humans in every region you target. With Reachtix's International SEO Services, your brand won't just be translated — it'll be optimized, targeted, and ready to win in every market.
                                </p>
                                <p className="sv-cta-description">
                                    Contact us today to start your global SEO journey and connect with audiences worldwide.
                                </p>
                                <div className="sv-cta-buttons">
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='primary'
                                    >
                                        <i className="fas fa-globe mr-2"></i>
                                        Start Global Expansion
                                    </Button>
                                    <Button
                                        tag={Link}
                                        to="/contact"
                                        color='dark'
                                    >
                                        <i className="fas fa-clipboard-list mr-2"></i>
                                        Get International Audit
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default InternationalSEOPage;